import { forwardRef, ReactElement, useMemo, useState, isValidElement } from 'react'
import { CustomContentProps, MaterialDesignContent, SnackbarContent } from 'notistack'
import { Box, Button, Stack, Typography } from '@mui/material'

const MAX_SNACKBAR_PREVIEW_CHARS = 200

const ExpandableSnackbarContent = forwardRef<HTMLDivElement, CustomContentProps>(
  ({ id, message, action, className, style, ...other }, ref): ReactElement => {
    const [detailsOpen, setDetailsOpen] = useState(false)

    const messageString = typeof message === 'string' ? message : null
    const shouldTruncate = (messageString?.length ?? 0) > MAX_SNACKBAR_PREVIEW_CHARS

    const previewMessage = useMemo(() => {
      if (!messageString) return null
      if (!shouldTruncate) return messageString
      return `${messageString.slice(0, MAX_SNACKBAR_PREVIEW_CHARS)}…`
    }, [messageString, shouldTruncate])

    const actionNode = typeof action === 'function' ? action(id) : action

    // If the message is already a custom SnackbarContent (e.g. CalculateErrorSnackbarContent),
    // render it as-is to avoid layout/padding regressions.
    const customContent = isValidElement(message) && message.type === SnackbarContent

    if (!messageString || !shouldTruncate || customContent) {
      return (
        <MaterialDesignContent
          ref={ref}
          id={id}
          className={className}
          style={style}
          {...other}
          message={message}
          action={action}
        />
      )
    }

    return (
      <MaterialDesignContent
        ref={ref}
        id={id}
        className={className}
        style={style}
        {...other}
        // We inline the action into the message block to keep the same
        // button positioning as CalculateErrorSnackbarContent.
        action={null}
        message={
          <Stack spacing={1} sx={{ maxWidth: 560, width: '100%' }}>
            {!detailsOpen ? (
              <Typography
                variant="body2"
                component="div"
                sx={{ wordBreak: 'break-word', whiteSpace: 'pre-wrap' }}
              >
                {previewMessage}
              </Typography>
            ) : (
              <Box sx={{ padding: 2 }}>
                <Typography
                  variant="body2"
                  component="div"
                  sx={{
                    wordBreak: 'break-word',
                    whiteSpace: 'pre-wrap',
                    maxHeight: 220,
                    overflow: 'auto',
                    pr: 0.5,
                  }}
                >
                  {messageString}
                </Typography>
              </Box>
            )}

            <Stack direction="row" flexWrap="wrap" gap={0.5} justifyContent="flex-end" width="100%">
              <Button
                color="inherit"
                size="small"
                onClick={() => setDetailsOpen(open => !open)}
                sx={{ textTransform: 'none' }}
              >
                {detailsOpen ? 'Weniger' : 'Mehr'}
              </Button>
              {actionNode ? <Box sx={{ display: 'contents' }}>{actionNode}</Box> : null}
            </Stack>
          </Stack>
        }
      />
    )
  },
)

ExpandableSnackbarContent.displayName = 'ExpandableSnackbarContent'

export default ExpandableSnackbarContent
